import { useEffect, useRef } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import api from '../services/api';

/**
 * Tracks authenticated page views. Posts to /api/track/pageview each time
 * the route changes. Errors are swallowed so tracking never disrupts UX.
 *
 * Tom 2026-05-09: usability research, not surveillance. Visible only to
 * the owner-only Login Activity tab.
 *
 * Normalizes paths with IDs to a route template, e.g.
 *   /projects/123      -> /projects/:id
 *   /change-orders/45  -> /change-orders/:id
 * so the aggregate counts roll up cleanly.
 */

// Add new patterns here as routes are added; the order matters (most
// specific first). The route template is what gets logged as `route`,
// and the original full path is logged as `path`.
const ROUTE_TEMPLATES = [
  '/projects/:id',
  '/engagements/:id',
  '/change-orders/:id',
  '/aftercare/:id',
  '/clients/:id',
  '/employees/:id',
  '/timeline/:id',
  '/proposals/:id',
  '/wip/:id',
];

function normalizeRoute(pathname) {
  for (const template of ROUTE_TEMPLATES) {
    if (matchPath({ path: template, end: true }, pathname)) {
      return template;
    }
  }
  return pathname;
}

export default function usePageViewTracking(enabled = true) {
  const location = useLocation();
  const lastSentRef = useRef({ route: null, ts: 0 });

  useEffect(() => {
    if (!enabled) return;
    const route = normalizeRoute(location.pathname);
    const path = location.pathname + (location.search || '');
    const referrer = lastSentRef.current.route || document.referrer || '';

    // Throttle: ignore identical-route fires within 1500ms (covers
    // double-renders / strict mode / hash changes).
    const now = Date.now();
    if (
      lastSentRef.current.route === route &&
      now - lastSentRef.current.ts < 1500
    ) {
      return;
    }
    lastSentRef.current = { route, ts: now };

    api
      .post('/track/pageview', { route, path, referrer })
      .catch(() => {
        /* silent — tracking must never break UX */
      });
  }, [enabled, location.pathname, location.search]);
}
